import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, CheckCircle, Truck, Home, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trackingSteps = [
  { key: 1, label: 'Order Received', icon: Package },
  { key: 2, label: 'Confirmed', icon: CheckCircle },
  { key: 3, label: 'Baking', icon: Package },
  { key: 4, label: 'Packed', icon: Package },
  { key: 5, label: 'Shipped', icon: Truck },
  { key: 6, label: 'Delivered', icon: Home },
];

export const TrackOrderPage = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState('');

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      setError('Please enter both order number and email');
      return;
    }

    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      const response = await axios.post(`${API}/orders/track`, {
        order_number: orderNumber.trim().toUpperCase(),
        email: email.trim().toLowerCase()
      });
      setOrderData(response.data);
    } catch (err) {
      console.error('Track order error:', err);
      const errorMsg = err.response?.data?.detail || 'Order not found. Please check your details.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStep = () => {
    if (!orderData?.current_step) return 1;
    return orderData.current_step.step;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-28 pb-16" data-testid="track-order-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-heading font-semibold text-[#2A1A14] mb-4">
            Track Your Order
          </h1>
          <p className="text-[#786C65]">
            Enter your order number and email to track your delicious cookies
          </p>
        </motion.div>

        {/* Search Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-[#E3DBD1] bg-white rounded-sm mb-8">
            <CardContent className="pt-6">
              <form onSubmit={handleTrackOrder} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="orderNumber" className="text-[#2A1A14]">
                      Order Number
                    </Label>
                    <Input
                      id="orderNumber"
                      placeholder="e.g., APR-ABC12345"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      className="mt-1 border-[#E3DBD1] focus:ring-[#7B3E19]"
                      data-testid="input-order-number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#2A1A14]">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 border-[#E3DBD1] focus:ring-[#7B3E19]"
                      data-testid="input-tracking-email"
                    />
                  </div>
                </div>
                {error && (
                  <p className="text-red-500 text-sm" data-testid="tracking-error">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm py-5"
                  data-testid="track-order-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Track Order
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Order Status */}
        {orderData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-[#E3DBD1] bg-white rounded-sm" data-testid="order-tracking-result">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#2A1A14] flex items-center justify-between">
                  <span>Order #{orderData.order_number}</span>
                  <span className="text-sm font-normal text-[#786C65]">
                    {new Date(orderData.created_at).toLocaleDateString()}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Progress Steps */}
                <div className="mb-8">
                  <div className="flex justify-between items-start relative">
                    {/* Progress Line */}
                    <div className="absolute top-5 left-0 right-0 h-1 bg-[#E3DBD1]">
                      <div 
                        className="h-full bg-[#7B3E19] transition-all duration-500"
                        style={{ width: `${((getCurrentStep() - 1) / (trackingSteps.length - 1)) * 100}%` }}
                      />
                    </div>
                    
                    {trackingSteps.map((step) => {
                      const isActive = step.key <= getCurrentStep();
                      const isCurrent = step.key === getCurrentStep();
                      const Icon = step.icon;
                      
                      return (
                        <div 
                          key={step.key} 
                          className="flex flex-col items-center relative z-10"
                          data-testid={`tracking-step-${step.key}`}
                        >
                          <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                              isActive 
                                ? 'bg-[#7B3E19] text-white' 
                                : 'bg-[#E3DBD1] text-[#786C65]'
                            } ${isCurrent ? 'ring-4 ring-[#7B3E19]/20' : ''}`}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`text-xs mt-2 text-center max-w-[60px] ${
                            isActive ? 'text-[#2A1A14] font-medium' : 'text-[#786C65]'
                          }`}>
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Current Status */}
                <div className="bg-[#EAE3D9] rounded-sm p-4 mb-6 text-center">
                  <p className="text-sm text-[#786C65]">Current Status</p>
                  <p className="text-lg font-heading font-semibold text-[#7B3E19]" data-testid="current-status">
                    {orderData.current_step?.label}
                  </p>
                  <p className="text-sm text-[#786C65] mt-1">
                    {orderData.current_step?.description}
                  </p>
                </div>

                {/* Order Items */}
                <div className="border-t border-[#E3DBD1] pt-4">
                  <h3 className="font-heading font-semibold text-[#2A1A14] mb-3">Order Items</h3>
                  <div className="space-y-2">
                    {orderData.items?.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-[#786C65]">{item.name} x{item.quantity}</span>
                        <span className="text-[#2A1A14]">${item.item_total?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between font-semibold mt-4 pt-4 border-t border-[#E3DBD1]">
                    <span className="text-[#2A1A14]">Total</span>
                    <span className="text-[#7B3E19]">${orderData.total?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="border-t border-[#E3DBD1] pt-4 mt-4">
                  <h3 className="font-heading font-semibold text-[#2A1A14] mb-2">Shipping Address</h3>
                  <p className="text-sm text-[#786C65]">{orderData.shipping_address}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};
